import React, { useState } from 'react';
import {
    useDownloadSelectedMatchesToPrint,
    useSearchBusyPlayers,
    useSearchMatchesInProgress,
    useSearchMatchesPending,
} from './matchesHooks';
import { useTournamentStore } from '../../service/useTournamentStore';
import { type BusyPlayer, type MatchDetail, MatchStatus } from '@iziping/common';
import MatchCardDetail from '../shared/MatchCardDetail';
import { useTranslation } from 'react-i18next';
import { CheckSquare2, Printer, Search, Square, Swords, X } from 'lucide-react';
import './match.css';
import { useNotification, InputIcon, EmptyState } from '@iziping/ui';

const MatchesManagement: React.FC = () => {
    const { selectedTournament } = useTournamentStore();
    const { t } = useTranslation();
    const { notify } = useNotification();
    const { data: matchesToLaunch } = useSearchMatchesPending({
        params: {
            tournamentId: selectedTournament!.id,
            status: MatchStatus.PENDING,
        },
    });

    const { data: matchesInProgress } = useSearchMatchesInProgress({
        params: {
            tournamentId: selectedTournament!.id,
            status: MatchStatus.RUNNING,
        },
    });

    const { data: busyPlayers } = useSearchBusyPlayers({ tournamentId: selectedTournament!.id });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMatchIds, setSelectedMatchIds] = useState<Set<number>>(new Set());

    const { mutateAsync: downloadSelected } = useDownloadSelectedMatchesToPrint();

    const term = searchTerm.toLowerCase();

    const matchesFilter = (match: MatchDetail) => {
        if (!term) return true;
        if (match.heatName.toLowerCase().includes(term)) return true;
        return match.opponents.some((o) =>
            o.heatRegistration.players.some(
                (p) => p.firstname.toLowerCase().includes(term) || p.lastname.toLowerCase().includes(term)
            )
        );
    };

    const dataLoaded = matchesToLaunch !== undefined && matchesInProgress !== undefined;

    const filteredInProgress = (matchesInProgress ?? []).filter(matchesFilter);
    const filteredPending = (matchesToLaunch ?? []).filter(matchesFilter);

    const bothEmpty = filteredInProgress.length === 0 && filteredPending.length === 0;

    const filteredBusyPlayers = (busyPlayers ?? []).filter(
        (bp: BusyPlayer) =>
            !term ||
            bp.match.heatName.toLowerCase().includes(term) ||
            bp.player.firstname.toLowerCase().includes(term) ||
            bp.player.lastname.toLowerCase().includes(term)
    );

    const handleToggleMatch = (id: number) => {
        setSelectedMatchIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const allRunningSelected =
        filteredInProgress.length > 0 && filteredInProgress.every((m) => selectedMatchIds.has(m.id));

    const handleToggleAll = () => {
        if (allRunningSelected) {
            setSelectedMatchIds(new Set());
        } else {
            setSelectedMatchIds(new Set(filteredInProgress.map((m) => m.id)));
        }
    };

    const openPdfBlob = (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    const handlePrintRunning = async () => {
        if (!selectedTournament?.id) return;
        try {
            const response = await downloadSelected({
                tournamentId: selectedTournament.id,
                matchIds: filteredInProgress.map((m) => m.id),
            });
            openPdfBlob(response.data);
        } catch {
            notify(t('pdf.download.error'), 'error');
        }
    };

    const handlePrintPending = async () => {
        if (!selectedTournament?.id) return;
        try {
            const response = await downloadSelected({
                tournamentId: selectedTournament.id,
                matchIds: filteredPending.map((m) => m.id),
            });
            openPdfBlob(response.data);
        } catch {
            notify(t('pdf.download.error'), 'error');
        }
    };

    const handlePrintSelected = async () => {
        if (!selectedTournament?.id) return;
        try {
            const response = await downloadSelected({
                tournamentId: selectedTournament.id,
                matchIds: Array.from(selectedMatchIds),
            });
            openPdfBlob(response.data);
        } catch {
            notify(t('pdf.download.error'), 'error');
        }
    };

    return (
        <div>
            <div className={'search-bar'}>
                <InputIcon
                    icon={Search}
                    placeholder={t('common.search') as string}
                    value={searchTerm}
                    small={true}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {filteredInProgress.length > 0 && (
                <>
                    <div className="section-header">
                        <h3>{t('MATCHES_RUNNING')}</h3>
                        <div className="section-actions">
                            <button className="btn-icon-text" onClick={handleToggleAll}>
                                {allRunningSelected ? <CheckSquare2 size={14} /> : <Square size={14} />}
                                {allRunningSelected ? t('matches.print.deselectAll') : t('matches.print.selectAll')}
                            </button>
                            <button
                                className={`btn-icon-text${selectedMatchIds.size > 0 ? ' btn-icon-text-primary' : ''}`}
                                onClick={selectedMatchIds.size > 0 ? handlePrintSelected : handlePrintRunning}
                            >
                                <Printer size={14} />
                                {selectedMatchIds.size > 0
                                    ? t('matches.print.selection', { count: selectedMatchIds.size })
                                    : t('matches.print.all')}
                            </button>
                            {selectedMatchIds.size > 0 && (
                                <button
                                    className="btn-icon-ghost"
                                    onClick={() => setSelectedMatchIds(new Set())}
                                    title={t('matches.print.clearSelection')}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={'container-item'}>
                        {filteredInProgress.map((match: MatchDetail) => (
                            <MatchCardDetail
                                key={match.id}
                                match={match}
                                isSelected={selectedMatchIds.has(match.id)}
                                onToggle={handleToggleMatch}
                            />
                        ))}
                    </div>
                </>
            )}

            {filteredPending.length > 0 && (
                <>
                    <div className="section-header">
                        <h3>{t('MATCHES_PENDING')}</h3>
                        <div className="section-actions">
                            <button className="btn-icon-text" onClick={handlePrintPending}>
                                <Printer size={14} />
                                {t('matches.print.all')}
                            </button>
                        </div>
                    </div>
                    <div className={'container-item'}>
                        {filteredPending.map((match: MatchDetail) => (
                            <MatchCardDetail key={match.id} match={match} />
                        ))}
                    </div>
                </>
            )}

            {dataLoaded && bothEmpty && <EmptyState icon={<Swords size={56} />} message={t('matches.empty')} />}

            <div className={'page'}>
                {filteredBusyPlayers.length > 0 && (
                    <table className={'table-stripped'}>
                        <thead>
                            <tr>
                                <th>{t('PLAYER_LASTNAME')}</th>
                                <th>{t('PLAYER_FIRSTNAME')}</th>
                                <th>{t('HEAT')}</th>
                                <th>{t('TABLE_NUMBER')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBusyPlayers.map((player: BusyPlayer) => (
                                <tr key={player.player.id}>
                                    <td>{player.player.lastname}</td>
                                    <td>{player.player.firstname}</td>
                                    <td>{player.match.heatName}</td>
                                    <td>{player.match.tableNumber}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default MatchesManagement;
